// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/BookingInvalidatedDialog.tsx
// 🔧 PURPOSE: Top-level blocking dialog that opens whenever a JWT-protected
//             channel manager call returns 403 with a `booking_*` errorCode
//             (no-show, cancelled+refunded, cancelled). The corresponding
//             session cleanup is owned by useGuestSession; this component
//             just informs the user.
//             Aspetto del mockup "MsSenzaAccesso": il foglio avorio dal
//             basso con il quadrato ambra → rosso, il titolo in Fraunces,
//             "CONTATTACI" (email alla struttura) e "CHIUDI" a filo. Chiude
//             la X, il bottone, Esc o un tocco fuori.
// ============================================

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Mail } from 'lucide-react';
import { cn } from '../lib/utils';
import StaySheet from './stay/StaySheet';
import { CreamClose, INK_TEXT, LINE_BUTTON, MUTED_TEXT, SERVICE_BUTTON } from './stay/ui';

type MessageKey = 'noShow' | 'cancelledRefunded' | 'cancelled' | 'default';

const messageKeyForErrorCode = (code: string | null): MessageKey => {
  switch (code) {
    case 'booking_no_show':       return 'noShow';
    case 'booking_cancelled_refunded': return 'cancelledRefunded';
    case 'booking_cancelled':     return 'cancelled';
    default:                      return 'default';
  }
};

const BookingInvalidatedDialog: React.FC = () => {
  const { t } = useTranslation();
  const [errorCode, setErrorCode] = useState<string | null>(null);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ errorCode?: string }>).detail;
      const code = detail?.errorCode || null;
      // Only render the dialog for booking_* codes — defensive in case the
      // event is dispatched with another shape in the future.
      if (code && code.startsWith('booking_')) {
        setErrorCode(code);
      }
    };
    window.addEventListener('nva_booking_invalidated', handler);
    return () => window.removeEventListener('nva_booking_invalidated', handler);
  }, []);

  const handleDismiss = () => setErrorCode(null);

  const key = messageKeyForErrorCode(errorCode);
  const contactEmail = t('bookingInvalidated.contactEmail');

  return (
    <StaySheet
      open={Boolean(errorCode)}
      onClose={handleDismiss}
      role="alertdialog"
      titleId="booking-invalidated-title"
      describedBy="booking-invalidated-message"
      closeLabel={t('bookingInvalidated.dismiss')}
      header={(
        <div className="relative flex justify-center pt-2">
          <span aria-hidden="true" className="h-1 w-10 rounded-full bg-[#D6CCB8] dark:bg-stone-700" />
          <CreamClose onClick={handleDismiss} label={t('bookingInvalidated.dismiss')} className="absolute right-3 top-3" />
        </div>
      )}
    >
      <div className="flex flex-col items-center px-4 pb-[22px] pt-4 text-center">
        <span
          aria-hidden="true"
          className="mt-6 flex h-[68px] w-[68px] items-center justify-center rounded-[20px] bg-gradient-to-br from-amber-500 to-red-500 text-white shadow-[0_12px_26px_rgba(239,68,68,.28)] dark:shadow-none"
        >
          <AlertTriangle className="h-8 w-8" strokeWidth={1.8} />
        </span>
        <h2 id="booking-invalidated-title" className={cn('mx-3 mb-0 mt-[18px] font-display text-[25px] font-bold leading-[1.15]', INK_TEXT)}>
          {t(`bookingInvalidated.title.${key}`)}
        </h2>
        <p id="booking-invalidated-message" className={cn('mx-1 mb-0 mt-2.5 text-[15px] leading-[1.5]', MUTED_TEXT)}>
          {t(`bookingInvalidated.message.${key}`)}
        </p>
        <div className="mt-[22px] flex w-full flex-col gap-2.5">
          <a
            href={`mailto:${contactEmail}`}
            className={cn(SERVICE_BUTTON, 'w-full bg-gradient-to-br from-blue-600 to-indigo-600 shadow-[0_8px_18px_rgba(37,99,235,.25)] dark:shadow-none')}
          >
            <Mail aria-hidden="true" className="h-[18px] w-[18px] flex-shrink-0" strokeWidth={2.2} />
            {t('bookingInvalidated.contactCta')}
          </a>
          <button type="button" onClick={handleDismiss} className={cn(LINE_BUTTON, 'w-full')}>
            {t('bookingInvalidated.dismiss')}
          </button>
        </div>
      </div>
    </StaySheet>
  );
};

export default BookingInvalidatedDialog;
